import { useEffect, useState } from "react";
import { Controller, FormProvider, type Path, useForm } from "react-hook-form";
import { authApi } from "api/auth/api";
import { getCountry } from "api/getCountry";
import cn from "classnames";
import { Button } from "components/ui/Button/Button";
import { CountriesSelect } from "components/ui/CountriesSelect";
import { FormField } from "components/ui/FormField";
import { Link } from "components/ui/Link";
import { Loader } from "components/ui/Loader/Loader";
import { PhoneSelect } from "components/ui/PhoneSelect/PhoneSelect";
import { SocialLogin } from "components/ui/SocialLogin/SocialLogin";
import { COUNTRIES_LIST, COUNTRY_DEFAULT_VALUES, COUNTRY_NAMES } from "constants/countries";
import { SIGN_IN_ROUTE, TERMS_AND_CONDITIONS_ROUTE } from "constants/routesValues";
import { SITE_NAME } from "constants/siteName";
import { VALIDATION_SCHEMA } from "constants/validationSchema";
import { useSiteName } from "context/SiteName.context";
import { createRouteAction } from "utils/history.utils";
import { getRecaptchaToken } from "utils/recaptcha";
import { EmailVerification } from "../EmailVerification";
import style from "./SignUp.module.scss";

export type SignUpFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  countryCode: string;
  phone: string;
  passwordConfirmation: string;
};

const isValidationErrorsResponse = (value: unknown): value is Record<string, string[]> => {
  if (typeof value !== "object" || value === null) return false;
  return Object.values(value).every((v) => Array.isArray(v) && v.every((item) => typeof item === "string"));
};

export const SignUp = () => {
  const { siteName } = useSiteName();
  const [isEmailVerification, setIsEmailVerification] = useState<boolean>(false);
  const [isSignUpFetching, setIsSignUpFetching] = useState<boolean>(false);
  const [isCountryLoading, setIsCountryLoading] = useState<boolean>(false);
  const [userCountry, setUserCountry] = useState(COUNTRY_DEFAULT_VALUES);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isChineseBrothers = siteName === SITE_NAME.CHINESE_BROTHERS;

  useEffect(() => {
    const fetchCountry = async () => {
      setIsCountryLoading(true);
      const countryInfo = await getCountry();
      const countryCodeFromApi = countryInfo?.country?.toUpperCase() || COUNTRY_DEFAULT_VALUES.countryCode;

      const matchedCountry =
        COUNTRIES_LIST.find((country) => country.countryCode.toUpperCase() === countryCodeFromApi) ||
        COUNTRY_DEFAULT_VALUES;

      setUserCountry(matchedCountry);
      setIsCountryLoading(false);
    };
    fetchCountry();
  }, []);

  const formMethods = useForm<SignUpFormValues>({
    defaultValues: {
      countryCode: COUNTRY_DEFAULT_VALUES.countryCode,
      phone: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      companyName: "",
      passwordConfirmation: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async (_data: SignUpFormValues) => {
    if (_data.password !== _data.passwordConfirmation) {
      formMethods.setError("passwordConfirmation", {
        message: "Passwords must be equal.",
      });

      return;
    } else {
      setIsSignUpFetching(true);
      setErrorMessage("");

      let recaptcha: string;
      try {
        recaptcha = await getRecaptchaToken("SIGNUP");
      } catch {
        setIsSignUpFetching(false);
        setErrorMessage("Error with reCAPTCHA verification.");
        return;
      }

      await authApi
        .signUp({
          first_name: _data.firstName,
          last_name: _data.lastName,
          email: _data.email,
          password: _data.password,
          company_name: _data.companyName,
          country_code: _data.countryCode,
          password_confirmation: _data.passwordConfirmation,
          phone: _data.phone,
          recaptcha,
        })
        .then((response) => {
          setIsSignUpFetching(false);
          if (response.status === 201) {
            setIsEmailVerification(true);
          }
        })
        .catch((error) => {
          setIsSignUpFetching(false);
          const status = error.response?.status;
          const errors = error.response?.data?.errors;

          if (status === 422 && isValidationErrorsResponse(errors)) {
            const isSignUpField = (field: string): field is Path<SignUpFormValues> => field in formMethods.getValues();

            Object.entries(errors).forEach(([field, messages]) => {
              if (field === "recaptcha") {
                setErrorMessage(messages[0] ?? "reCAPTCHA verification failed.");
                return;
              }
              if (isSignUpField(field)) {
                formMethods.setError(field, {
                  message: messages[0],
                });
              }
            });
            return;
          }

          if (status === 500) {
            setErrorMessage("Server error. Please try again later.");
          }

          setErrorMessage(error.response?.data?.message || "Something went wrong.");
        });
    }
  };

  return isEmailVerification ? (
    <EmailVerification email={formMethods.getValues("email")} />
  ) : (
    <div
      className={cn(style.signUpWrapper, {
        [style.signUpWrapperChineseBrothers]: isChineseBrothers,
      })}
    >
      <div className={style.signUpInner}>
        <h3 className={style.signUpTitle}>Sign up</h3>
        <FormProvider {...formMethods}>
          <form className={style.signUpForm} onSubmit={formMethods.handleSubmit(onSubmit)} method="POST">
            <div className={style.signUpFields}>
              <div className={style.signUpRowTwo}>
                <Controller
                  name="firstName"
                  control={formMethods.control}
                  rules={VALIDATION_SCHEMA.firstName}
                  render={({ field, fieldState }) => (
                    <FormField
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      labelTitle="First name"
                      type="text"
                      className={style.signUpFieldCol}
                      error={fieldState.error}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={formMethods.control}
                  render={({ field, fieldState }) => (
                    <FormField
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      labelTitle="Last name"
                      type="text"
                      className={style.signUpFieldCol}
                      error={fieldState.error}
                    />
                  )}
                />
              </div>
              <div className={style.signUpRowFull}>
                <Controller
                  name="email"
                  control={formMethods.control}
                  rules={VALIDATION_SCHEMA.email}
                  render={({ field, fieldState }) => (
                    <FormField
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      labelTitle="email"
                      type="email"
                      className={style.signUpFieldCol}
                      error={fieldState.error}
                    />
                  )}
                />
              </div>
              <div className={style.signUpRowTwo}>
                <Controller
                  name="companyName"
                  control={formMethods.control}
                  rules={VALIDATION_SCHEMA.companyName}
                  render={({ field, fieldState }) => (
                    <FormField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      labelTitle="company name"
                      type="text"
                      className={style.signUpFieldCol}
                      error={fieldState.error}
                    />
                  )}
                />
                <CountriesSelect
                  userCountry={userCountry}
                  countries={COUNTRY_NAMES}
                  isCountryLoading={isCountryLoading}
                  className={style.countriesSelect}
                />
              </div>
              <PhoneSelect
                userCountry={userCountry}
                className={style.signUpControl}
                isCountryLoading={isCountryLoading}
              />
              <Controller
                name="password"
                control={formMethods.control}
                rules={VALIDATION_SCHEMA.password}
                render={({ field, fieldState }) => (
                  <FormField
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    labelTitle="password"
                    type="password"
                    isPassword
                    className={style.signUpFieldCol}
                    error={fieldState.error}
                  />
                )}
              />
              <Controller
                name="passwordConfirmation"
                control={formMethods.control}
                rules={VALIDATION_SCHEMA.passwordConfirmation}
                render={({ field, fieldState }) => (
                  <FormField
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    labelTitle="password confirmation"
                    type="password"
                    isPassword
                    className={style.signUpFieldCol}
                    error={fieldState.error}
                  />
                )}
              />
              <p className={style.signUpTerms}>
                By signing in or creating an account, you agree with our{" "}
                <Link
                  className={style.signUpTermsLink}
                  href={TERMS_AND_CONDITIONS_ROUTE}
                  text="Terms of Service"
                  target="_blank"
                />
              </p>
            </div>
            <Button
              disabled={isSignUpFetching}
              className={cn(style.formButton, {
                [style.formButtonChineseBrothers]: isChineseBrothers,
              })}
              type="submit"
              text={isSignUpFetching ? "Please wait" : "Create account"}
            >
              {isSignUpFetching && <Loader className={style.loader} />}
            </Button>
            {errorMessage && <p className={style.errorMessage}>{errorMessage}</p>}
          </form>
        </FormProvider>
        <span className={style.continueWith}>Or continue with</span>
        <SocialLogin />
        <p className={style.formDescriptionLink}>
          Back to <span onClick={() => createRouteAction(SIGN_IN_ROUTE)}>Sign in</span>
        </p>
      </div>
    </div>
  );
};
